import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { get, maxBy } from 'lodash';
import tableMessages from 'containers/QuestionnairePage/MatrixAndConceptsTable/messages';
import { isMatrixSingleOrBipolarQuestionType } from 'utils/nodeTypes';
import { FormattedMessage } from 'react-intl';
import QuestionFooter from 'containers/QuestionnairePage/GenericQuestion/QuestionFooter';
import { QUESTION_ICON_MAP } from 'containers/TemplatePreviewPage/QuestionsListViewOnly/GenericQuestionViewOnly/constants';
import QuestionPrompt from '../QuestionPromptViewOnly';
import Table from './MatrixAndConceptsTableViewOnly/index';

const MatrixQuestionViewOnly = ({ renderPrompt, question, attributes }) => {
  const { answers = [], scorepoints = [], refId, type, isActive } = question;
  const isSingleChoice = isMatrixSingleOrBipolarQuestionType(type);
  const [tableCellColor, setTableCellColor] = useState(
    'force-background-grey-2',
  );

  const tablefixedColumnWidth = useMemo(() => {
    const maxByLength =
      get(
        maxBy(scorepoints, ({ body }) => body.length),
        'body.length',
        0,
      ) + 180;
    return Math.min(240, maxByLength + 5);
  }, [scorepoints]);

  const tableColumnWidth = useMemo(() => {
    const maxByLength =
      get(
        maxBy(answers, ({ body }) => body.length),
        'body.length',
        0,
      ) + 240;
    return Math.min(300, maxByLength);
  }, [answers]);

  return (
    <div
      onMouseEnter={() => setTableCellColor('force-background-grey-1')}
      onMouseLeave={() => setTableCellColor('force-background-grey-2')}
    >
      <QuestionPrompt
        className="margin-btm-sm"
        icon={QUESTION_ICON_MAP[question.type]}
        renderPrompt={renderPrompt}
        questionIndex={question.questionIndex}
        label={question.label}
      />
      <div className="order-radius-level-1 padding-top-sm margin-btm-md">
        <p className="text-weight-bold text-size-secondary color-grey-10 padding-horizontal-sm margin-btm-sm">
          <FormattedMessage {...tableMessages.matrix.title} />
        </p>

        <Table
          tableId={`table-${refId}`}
          tableType="matrix"
          inputType="formattedInput"
          tableRows={answers}
          cellColor={tableCellColor}
          tableColumns={scorepoints}
          tableColumnWidth={tableColumnWidth}
          tablefixedColumnWidth={tablefixedColumnWidth}
          renderCellContent={() => (
            <div className="flex flex-items-center flex-justify-center">
              <div
                className="border-grey-7"
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: isSingleChoice ? 16 : 1,
                }}
              />
            </div>
          )}
          isActive={isActive}
        />
      </div>
      <QuestionFooter
        key={`${question.refId}-${question.type}-footer`}
        type={question.type}
        label={question.label}
        goesTo={question.goesTo}
        goesToWithErrors={question.goesToWithErrors}
        comesFrom={question.comesFrom}
        isUnused={question.isUnused}
        invalidReferences={question.invalidReferences}
        invalidReferencesAnswersLogic={question.invalidReferencesAnswersLogic}
        invalidReferencesVariablesLogic={
          question.invalidReferencesVariablesLogic
        }
        media={question.media}
        shuffle={question.shuffle}
        ratingSize={question.ratingSize}
        selectedAttributes={question.selectedAttributes}
        rows={question.answers}
        attributes={attributes}
        hiddenVariables={question.hiddenVariables}
        readOnly
      />
    </div>
  );
};

MatrixQuestionViewOnly.propTypes = {
  question: PropTypes.object.isRequired,
  renderPrompt: PropTypes.func.isRequired,
};

export default MatrixQuestionViewOnly;
